/*
 * ─────────────────────────────────────────────────────────────────────────────
 * VECCHI FILE GESTIONE CORSI — sostituiti dalla gestione in
 * src/jsx/pages/struttura/corsi/. Non sono più raggiungibili dal menu: restano
 * solo per gli indirizzi diretti, finché non si decide di eliminarli.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import React, { useState, useEffect } from "react";
import { Row, Col, Card, Tab, Nav, Modal, Button, Spinner, Form, Alert } from "react-bootstrap";
import { Information } from "iconsax-react";
import { Link, useParams, useNavigate } from 'react-router-dom';
import MyApiClient from "../../../../../api-client/MyApiClient";
import Select from "react-select";
import { sidSelectStyles } from "../../../../components/Util/sidSelectStyles";
import { usePageTitle } from "../../../../layouts/PageHeaderExtra";
import { useStatoNelTitolo } from "../../../../components/Util/StatoProdotto";
import { sports, tipologiaStruttura } from "../../../../components/Util/enum";
import { useProfile } from "../../../../../hooks/useProfile";
import ProductImageUploader from "../../../../components/Util/ProductImageUploader";
import { VAT_OPTIONS, findVatOption, vatFieldsFromOption } from "../../../../components/Util/vatOptions";

import { conMotivo } from "../../../../components/Util/erroreServer";
/*
 * Bozza di un corso multiplo, con lo stesso aspetto della scheda corso (view.js): un solo box a
 * sezioni (Informazioni, Prezzi, Condizioni), interruttori per i si'/no, stato accanto agli altri
 * dati. Prima era un percorso a quattro passi (Informazioni -> Prezzi -> Condizioni -> Riepilogo).
 * "Salva bozza" chiede solo il nome; "Salva e attiva" fa gli stessi controlli del vecchio
 * percorso prima di pubblicare il corso.
 */
export const CoursesEdit = () => {

    const navigate = useNavigate();
    const { id } = useParams();

    const { profile: profileData } = useProfile({ autoFetch: true, includeTitles: true });

    const sdiEnabledNoVat = profileData?.club?.invoiceDataSender?.invoice_enabled === true && !profileData?.club?.invoiceDataSender?.vat_number;
    const billingNotConfirmed = profileData && profileData?.club?.invoiceDataSender?.status !== 'CONFIRMED';
    // Prezzo standard e IVA bloccati come nel vecchio percorso: senza partita IVA o senza documenti fiscali validati
    const prezzoBloccato = sdiEnabledNoVat || billingNotConfirmed;
    const motivoBlocco = sdiEnabledNoVat
        ? 'Prezzo standard e IVA non modificabili senza partita IVA configurata'
        : 'Prezzo standard e IVA modificabili dopo la validazione dei documenti fiscali';

    const [loadingShowed, setLoadingShowed] = useState(false);

    const [confirmShowed, setConfirmShowed] = useState(false);
    const [onContinue, setOnContinue] = useState(null);
    const [confirmText, setConfirmText] = useState('');

    const [modalShowed, setModalShowed] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [modalText, setModalText] = useState('');

    const [pictureModalShowed, setPictureModalShowed] = useState(false);

    const [enabledSports, setEnabledSports] = useState([]);
    const [showingSociOptions, setShowingSociOptions] = useState(false);
    const [showingTesseratiOptions, setShowingTesseratiOptions] = useState(false);
    const [paymentMethods, setPaymentMethods] = useState({
        stripe_enabled: false,
        bank_transfer_enabled: false
    });

    const [formData, setFormData] = useState({
        id: '',
        name: '',
        sportType: '',
        maxMembers: 0,
        standard_price: 0,
        member_price: 0,
        federated_price: 0,
        fitcoin_cashback: 0,
        payable_in_club: false,
        payable_stripe: false,
        payable_bank: false,
        vat_rate: 22,
        vat_nature: undefined,
        iva_5_percent: false,
        newCoursePicture: '',
        coursePicture: '',
        description: '',
        status: 'DRAFT',
        type: 'OPEN'
    });

    usePageTitle(formData?.name ? `Bozza corso: ${formData.name}` : null);
    // Stato accanto al titolo: è una bozza (si attiva con «Salva e attiva»)
    useStatoNelTitolo({ bozza: true });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const tokenDetailsString = sessionStorage.getItem('userDetails');
                if (tokenDetailsString) {
                    const token = JSON.parse(tokenDetailsString).idToken;
                    const apiClient = new MyApiClient();

                    setLoadingShowed(true);
                    const corso = await apiClient.getBookingCourseById(token, id);
                    setLoadingShowed(false);

                    if (corso) {
                        setFormData({
                            id: corso.id,
                            name: corso.name,
                            sportType: corso.sportType,
                            maxMembers: corso.maxMembers || 0,
                            standard_price: corso.standard_price,
                            member_price: corso.member_price,
                            federated_price: corso.federated_price,
                            fitcoin_cashback: corso.fitcoin_cashback,
                            payable_in_club: corso.payable_in_club,
                            payable_stripe: corso.payable_stripe,
                            payable_bank: corso.payable_bank,
                            vat_rate: corso.vat_rate,
                            vat_nature: corso.vat_nature,
                            iva_5_percent: corso.iva_5_percent,
                            status: corso.status,
                            description: corso.description,
                            coursePicture: corso.coursePicture,
                            newCoursePicture: '',
                            type: 'OPEN'
                        });
                    }
                }
            } catch (error) {
                setLoadingShowed(false);
                console.error("Error fetching data:", error);
            }
        };

        fetchData();

        const tokenDetailsString = sessionStorage.getItem('userDetails');
        if (tokenDetailsString) {
            const tokenDetails = JSON.parse(tokenDetailsString);
            if (tokenDetails.clubNature === tipologiaStruttura.ASD.value) {
                setShowingSociOptions(true);
            }
            if (tokenDetails.clubNature === tipologiaStruttura.ASD.value || tokenDetails.clubNature === tipologiaStruttura.SSD.value) {
                setShowingTesseratiOptions(true);
            }
            setPaymentMethods({
                stripe_enabled: tokenDetails.stripe_enabled || false,
                bank_transfer_enabled: tokenDetails.bank_transfer_enabled || false
            });
        }
    }, [id]);

    useEffect(() => {
        if (profileData && profileData.club && profileData.club.sports) {
            const clubSports = profileData.club.sports.map(s => s.sportType);
            setEnabledSports(sports.filter(element => clubSports.includes(element.value)));
        }
    }, [profileData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const mostraErrore = (titolo, testo) => {
        setModalTitle(titolo);
        setModalText(testo);
        setModalShowed(true);
    };

    // L'immagine viaggia come { content, name }: la si invia solo se ne e' stata
    // scelta una nuova, altrimenti si omette il campo (un content null oppure un
    // oggetto gia' incapsulato faceva fallire lo schema con "must be string").
    const preparePicturePayload = (payload, field) => {
        const current = payload[field];
        const content = typeof current === 'string' ? current : current?.content;
        if (content) {
            payload[field] = {
                content: content,
                name: Math.random().toString(36).substring(2, 10) + (new Date()).getTime().toString(36),
            };
        } else {
            delete payload[field];
        }
    };

    // Il corso come lo vuole il server, sempre in bozza: l'attivazione e' una chiamata a parte.
    const payloadBozza = () => {
        const tokenDetails = JSON.parse(sessionStorage.getItem('userDetails') || '{}');
        const payload = { ...formData, clubId: tokenDetails.localId, status: 'DRAFT' };

        if (!showingSociOptions) {
            payload.grants_club_membership = false;
            payload.member_price = payload.standard_price;
        }
        if (!showingTesseratiOptions) {
            payload.grants_federated_status = false;
            payload.federated_price = payload.standard_price;
        }

        preparePicturePayload(payload, 'newCoursePicture');
        Object.keys(payload).forEach(key => { if (payload[key] === '') delete payload[key]; });
        return payload;
    };

    const salvaBozza = async () => {
        if (!formData.name || formData.name.trim() === '') {
            mostraErrore("Errore di validazione", "Devi inserire il nome per salvare la bozza");
            return;
        }
        try {
            const token = JSON.parse(sessionStorage.getItem('userDetails') || '{}').idToken;
            if (!token) return;
            const apiClient = new MyApiClient();

            setLoadingShowed(true);
            const result = await apiClient.updateBookingCourse(token, payloadBozza());
            setLoadingShowed(false);

            if (result && result.error) {
                mostraErrore("Errore durante l'operazione", result.error);
            } else {
                navigate('/struttura/booking/courses');
            }
        } catch (error) {
            setLoadingShowed(false);
            console.error('Error in salvaBozza: ' + error);
            mostraErrore("Errore durante l'operazione", conMotivo("Si è verificato un errore durante il salvataggio della bozza"));
        }
    };

    // Gli stessi controlli del vecchio percorso a passi, tutti insieme: il primo che manca
    // viene detto in chiaro.
    const cosaManca = () => {
        const numero = (v) => parseFloat(String(v ?? '').replace(',', '.'));
        if (!formData.name || !formData.name.trim()) return "Inserisci il nome del corso.";
        if (!formData.sportType) return "Scegli la tipologia di sport.";
        if (!(parseInt(formData.maxMembers, 10) > 0)) return "Il numero di partecipanti deve essere maggiore di 0.";
        if (!formData.newCoursePicture && !formData.coursePicture) return "Carica la foto del corso.";
        if (!prezzoBloccato && !(numero(formData.standard_price) > 0)) return "Il prezzo standard deve essere maggiore di 0.";
        if (showingTesseratiOptions && !(numero(formData.federated_price) > 0)) return "Il prezzo tesserati / soci deve essere maggiore di 0.";
        if (!(numero(formData.fitcoin_cashback) >= 0)) return "Il cashback Fitcoin deve essere maggiore o uguale a 0.";
        if (!formData.payable_in_club && !formData.payable_stripe && !formData.payable_bank) {
            return "Almeno uno dei metodi di pagamento deve essere attivo (in struttura, Stripe o bonifico).";
        }
        return null;
    };

    const salvaEAttiva = () => {
        if (billingNotConfirmed) {
            mostraErrore("Documenti fiscali non ancora validati", "Non è possibile attivare la vendita di questo prodotto finché i documenti fiscali della tua struttura non sono stati validati. Per pubblicare prodotti e renderli acquistabili nell'App SportId Player devi prima configurare e confermare il metodo di fatturazione nella sezione La mia struttura → Documenti fiscali. Nel frattempo puoi continuare a creare prodotti e salvarli come bozza.");
            return;
        }
        const manca = cosaManca();
        if (manca) {
            mostraErrore("Dati mancanti", manca);
            return;
        }
        setOnContinue(() => () => continuaSalvaEAttiva());
        setConfirmShowed(true);
        setConfirmText("Sei sicuro di volere continuare? Il corso sarà attivato e sarà visibile agli utenti che cercano la tua struttura sull'app SportID.");
    };

    const continuaSalvaEAttiva = async () => {
        setConfirmShowed(false);
        try {
            const token = JSON.parse(sessionStorage.getItem('userDetails') || '{}').idToken;
            if (!token) return;
            const apiClient = new MyApiClient();
            const payload = payloadBozza();

            setLoadingShowed(true);
            const result = await apiClient.updateBookingCourse(token, payload);
            if (result && result.error) {
                setLoadingShowed(false);
                mostraErrore("Errore durante l'operazione", result.error);
                return;
            }

            const attivazione = await apiClient.changeCourseStatus(token, payload.id, 'PURCHASABLE');
            setLoadingShowed(false);

            if (attivazione && attivazione.error) {
                mostraErrore("Errore durante l'attivazione", attivazione.error);
            } else {
                navigate('/struttura/booking/courses');
            }
        } catch (error) {
            setLoadingShowed(false);
            console.error('Error in continuaSalvaEAttiva: ' + error);
            mostraErrore("Errore durante l'operazione", "Errore generico durante il salvataggio e attivazione");
        }
    };

    // Eliminazione della bozza: il backend la consente solo in stato DRAFT,
    // ed è proprio lo stato in cui si apre questa pagina.
    const eliminaBozza = () => {
        setOnContinue(() => () => continuaEliminaBozza());
        setConfirmShowed(true);
        setConfirmText("Sei sicuro di volere continuare? La bozza del corso sarà eliminata dalla tua struttura.");
    };

    const continuaEliminaBozza = async () => {
        setConfirmShowed(false);
        try {
            const token = JSON.parse(sessionStorage.getItem('userDetails') || '{}').idToken;
            if (!token) return;
            const apiClient = new MyApiClient();

            setLoadingShowed(true);
            const result = await apiClient.deleteBookingCourse(token, id);
            setLoadingShowed(false);

            if (!result || result.error) {
                mostraErrore("Eliminazione non riuscita", result && result.error ? result.error : conMotivo("Si è verificato un errore durante la cancellazione"));
            } else {
                navigate('/struttura/booking/courses');
            }
        } catch (error) {
            setLoadingShowed(false);
            console.error('Error in continuaEliminaBozza: ' + error);
            mostraErrore("Eliminazione non riuscita", conMotivo("Si è verificato un errore durante la cancellazione"));
        }
    };

    const rimuoviFoto = () => setFormData(prev => ({ ...prev, newCoursePicture: '', coursePicture: '' }));

    const campoTesto = (fieldName, label, type = 'text', bloccato = false) => (
        <div title={bloccato ? motivoBlocco : undefined}>
            <Form.Label className="mb-2">{label}</Form.Label>
            {type === 'textarea' ? (
                <Form.Control as="textarea" rows={10} className="mt-2" name={fieldName} value={formData[fieldName] || ''} onChange={handleChange} />
            ) : (
                <Form.Control
                    disabled={bloccato}
                    className={`mt-2${bloccato ? ' sid-ro-input' : ''}`}
                    type={type}
                    name={fieldName}
                    value={(formData[fieldName] || formData[fieldName] === 0) ? formData[fieldName] : ''}
                    onChange={handleChange}
                />
            )}
        </div>
    );

    const interruttore = (fieldName, label) => (
        <div className="sid-switch-row">
            <Form.Check
                type="switch"
                id={`switch-${fieldName}`}
                label={label}
                checked={!!formData[fieldName]}
                onChange={(e) => setFormData(prev => ({ ...prev, [fieldName]: e.target.checked }))}
            />
        </div>
    );

    const foto = formData.newCoursePicture
        ? `data:image/jpeg;base64,${formData.newCoursePicture}`
        : formData.coursePicture;

    return (
        <>
            <Modal className="fade" show={confirmShowed} centered>
                <Modal.Header>
                    <Modal.Title>Sei sicuro?</Modal.Title>
                    <Button onClick={() => setConfirmShowed(false)} variant="" className="btn-close"></Button>
                </Modal.Header>
                <Modal.Body>
                    <Row><Col className="col-xl-64">{confirmText}</Col></Row>
                </Modal.Body>
                <Modal.Footer className="justify-content-between">
                    <Button className="btn btn-light btn-add" onClick={() => setConfirmShowed(false)}>Chiudi</Button>
                    <Button className="btn btn-primary btn-add" onClick={() => { setConfirmShowed(false); if (onContinue) onContinue(); }}>Continua</Button>
                </Modal.Footer>
            </Modal>

            <Modal className="fade" show={modalShowed} centered>
                <Modal.Header>
                    <Modal.Title>{modalTitle}</Modal.Title>
                    <Button onClick={() => setModalShowed(false)} variant="" className="btn-close"></Button>
                </Modal.Header>
                <Modal.Body>
                    <Row><Col className="col-xl-64">{modalText}</Col></Row>
                </Modal.Body>
                <Modal.Footer className="justify-content-between">
                    <Button className="btn btn-primary btn-add" onClick={() => setModalShowed(false)}>Chiudi</Button>
                </Modal.Footer>
            </Modal>

            <ProductImageUploader
                show={pictureModalShowed}
                onClose={() => setPictureModalShowed(false)}
                onSave={(base64String) => {
                    setPictureModalShowed(false);
                    setFormData(prev => ({ ...prev, newCoursePicture: base64String }));
                }}
                titolo="Immagine del corso"
            />

            <Modal className="fade" show={loadingShowed} centered>
                <Modal.Body className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="sr-only">Loading...</span>
                    </Spinner>
                    <div>Caricamento in corso...</div>
                </Modal.Body>
            </Modal>

            {billingNotConfirmed && (
                <Alert variant="warning" className="d-flex align-items-start gap-2 text-dark" style={{ marginBottom: '1rem' }}>
                    <Information size="20" className="mt-1 flex-shrink-0" />
                    <div>
                        <strong>Metodo di fatturazione non configurato.</strong> Per pubblicare prodotti e renderli acquistabili nell'App SportId Player è necessario configurare e confermare il metodo di fatturazione nella sezione <strong>La mia struttura → Documenti fiscali</strong>. Puoi continuare a creare prodotti e salvarli come bozza.
                    </div>
                </Alert>
            )}

            <Row className="align-items-start mt-0">
                <div className="col-xl-12">
                    <div className="custom-tab-1">
                        <Tab.Container defaultActiveKey="informazioni">
                            <Nav as="ul" className="nav-tabs" style={{ border: 'none' }}>
                                <Nav.Item as="li" className="tabMenu">
                                    <Nav.Link eventKey="informazioni">Informazioni generali</Nav.Link>
                                </Nav.Item>
                            </Nav>
                            <Row className="align-items-start mt-4">
                                <div className="col-12">
                                    <Tab.Content>
                                        <Tab.Pane eventKey="informazioni">
                                            <Card>
                                                <Card.Body className="sid-card-body--spaced">
                                                    <Row className="g-4">
                                                        <Col md={6}>
                                                            <Form.Label>Tipo</Form.Label>
                                                            <div className="sid-readonly-value">Multiplo</div>
                                                        </Col>
                                                        <Col md={6}>
                                                            {campoTesto("name", "Nome corso")}
                                                        </Col>
                                                        <Col md={3}>
                                                            <Form.Label className="mb-2">Tipologia di sport</Form.Label>
                                                            <Select
                                                                className="mt-2"
                                                                styles={sidSelectStyles}
                                                                name="sportType"
                                                                value={enabledSports.find(option => option.value === formData.sportType) || null}
                                                                onChange={(selectedOption) => setFormData(prev => ({ ...prev, sportType: selectedOption?.value || '' }))}
                                                                options={enabledSports}
                                                            />
                                                        </Col>
                                                        <Col md={3}>
                                                            {campoTesto("maxMembers", "Partecipanti massimi", "number")}
                                                        </Col>
                                                        <Col md={12}>
                                                            {campoTesto("description", "Descrizione", "textarea")}
                                                        </Col>
                                                        <Col md={12}>
                                                            <Form.Label>Foto</Form.Label>
                                                            <div className="sid-photo-field">
                                                                <div className="sid-photo-field__preview">
                                                                    {foto ? <img src={foto} alt="" /> : <span>Nessuna foto</span>}
                                                                </div>
                                                                <div className="d-flex flex-wrap gap-2">
                                                                    <Button size="sm" variant="outline-dark" onClick={() => setPictureModalShowed(true)}>
                                                                        {foto ? 'Sostituisci foto' : 'Carica foto'}
                                                                    </Button>
                                                                    {foto && (
                                                                        <Button size="sm" variant="outline-danger" onClick={rimuoviFoto}>Rimuovi foto</Button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </Col>
                                                    </Row>

                                                    <h5 className="sid-form-section">Prezzi</h5>
                                                    <Row className="g-4">
                                                        <Col md={3}>
                                                            {campoTesto("standard_price", "Prezzo standard", "number", prezzoBloccato)}
                                                        </Col>
                                                        <Col md={3}>
                                                            <div title={prezzoBloccato ? motivoBlocco : undefined}>
                                                                <Form.Label className="mb-2">IVA</Form.Label>
                                                                <Select
                                                                    className="mt-2"
                                                                    styles={sidSelectStyles}
                                                                    isDisabled={prezzoBloccato}
                                                                    name="vat_rate"
                                                                    value={findVatOption(formData)}
                                                                    onChange={(selectedOption) => setFormData(prev => ({ ...prev, ...vatFieldsFromOption(selectedOption) }))}
                                                                    options={VAT_OPTIONS}
                                                                />
                                                            </div>
                                                        </Col>
                                                        {showingTesseratiOptions && (
                                                            <Col md={3}>
                                                                {campoTesto("federated_price", "Prezzo tesserati / soci", "number")}
                                                            </Col>
                                                        )}
                                                        <Col md={3}>
                                                            {campoTesto("fitcoin_cashback", "Cashback Fitcoin", "number")}
                                                        </Col>
                                                    </Row>

                                                    <h5 className="sid-form-section">Forme di pagamento</h5>
                                                    <div className="sid-switch-inline">
                                                        {interruttore("payable_in_club", "Acquisto pagabile in struttura")}
                                                        {paymentMethods.stripe_enabled && interruttore("payable_stripe", "Acquisto pagabile su Stripe")}
                                                        {paymentMethods.bank_transfer_enabled && interruttore("payable_bank", "Acquisto pagabile con bonifico")}
                                                    </div>
                                                </Card.Body>
                                            </Card>

                                            <div className="d-flex justify-content-between align-items-center gap-4 mt-4">
                                                <Link onClick={() => eliminaBozza()} to="#" className="btn btn-danger light">Elimina bozza</Link>
                                                <div className="d-flex gap-4">
                                                    <button type="button" className="btn btn-dark light" onClick={() => navigate('/struttura/booking/courses')}>Indietro</button>
                                                    <Button onClick={salvaBozza} className="btn btn-secondary">Salva bozza</Button>
                                                    <Button onClick={salvaEAttiva} className="btn btn-primary">Salva e attiva</Button>
                                                </div>
                                            </div>
                                        </Tab.Pane>
                                    </Tab.Content>
                                </div>
                            </Row>
                        </Tab.Container>
                    </div>
                </div>
            </Row>
        </>
    );
};
export default CoursesEdit;
